import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { isEmpty } from "lodash";
import { PropTypes } from "prop-types";
import { Button, Popconfirm, Tooltip, Row, Col, Typography } from "antd";
import moment from "moment";
import { FlagOutlined, MenuOutlined } from "@ant-design/icons";
import CustomPropTypes from "@/customPropTypes";
import { formatDate, formatDateTime } from "@mds/common/redux/utils/helpers";
import { openModal, closeModal } from "@mds/common/redux/actions/modalActions";
import {
  getNoticeOfWorkApplicationDocumentTypeOptionsHash,
  getDropdownNoticeOfWorkApplicationDocumentTypeOptions,
} from "@mds/common/redux/selectors/staticContentSelectors";
import {
  getNoticeOfWork,
  getApplicationDelay,
} from "@mds/common/redux/selectors/noticeOfWorkSelectors";
import { getDraftPermitAmendmentForNOW } from "@mds/common/redux/selectors/permitSelectors";
import { getUserAccessData } from "@mds/common/redux/selectors/authenticationSelectors";
import {
  comparePermitPackageDocuments,
  compareByTypeThenOrder,
  getPermitPackageOrderLabel,
  isFileReferencedInConditions,
} from "@mds/common/utils/permitPackageDocuments";
import {
  fetchImportedNoticeOfWorkApplication,
  updateNoticeOfWorkApplication,
  deleteNoticeOfWorkApplicationDocument,
  editNoticeOfWorkDocument,
  sortNoticeOfWorkDocuments,
  createNoticeOfWorkDocumentVersion,
  archiveNoticeOfWorkDocuments,
} from "@mds/common/redux/actionCreators/noticeOfWorkActionCreator";
import { documentWithTag } from "@mds/common/components/documents/DocumentColumns";
import { openDocument } from "@mds/common/components/syncfusion/DocumentViewer";
import { downloadFileFromDocumentManager } from "@mds/common/redux/utils/actionlessNetworkCalls";
import * as Strings from "@mds/common/constants/strings";
import DocumentLink from "@mds/common/components/documents/DocumentLink";
import AddButton from "@/components/common/buttons/AddButton";
import { modalConfig } from "@/components/modalContent/config";
import * as Permission from "@/constants/permissions";
import NOWActionWrapper from "@/components/noticeOfWork/NOWActionWrapper";
import { EDIT_OUTLINE_VIOLET, TRASHCAN } from "@/constants/assets";
import ReferralConsultationPackage from "@/components/noticeOfWork/applications/referals/ReferralConsultationPackage";
import PermitPackage from "@/components/noticeOfWork/applications/PermitPackage";
import { sortableContainer, sortableElement, sortableHandle } from "react-sortable-hoc";
import arrayMove from "array-move";
import CoreTable from "@mds/common/components/common/CoreTable";
import { renderPermitPackageTitleColumn } from "@/components/noticeOfWork/applications/permitPackageTitleColumn";
import NOWDownloadAllButton from "@/components/noticeOfWork/applications/NOWDownloadAllButton";
import {
  fileManagementActionsColumn,
  fileNameHeaderCell,
  getFileManagementContext,
  getFileNameOffset,
  isVersionRow,
  openArchiveFileModal,
  openReplaceFileModal,
  toFileManagementRow,
} from "@/components/noticeOfWork/applications/nowFileManagement";

const DragHandle = sortableHandle(() => <MenuOutlined style={{ cursor: "grab", color: "#999" }} />);

const propTypes = {
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  noticeOfWork: CustomPropTypes.importedNOWApplication.isRequired,
  documents: PropTypes.arrayOf(PropTypes.any).isRequired,
  noticeOfWorkApplicationDocumentTypeOptionsHash: PropTypes.objectOf(PropTypes.any).isRequired,
  noticeOfWorkApplicationDocumentTypeOptions: PropTypes.objectOf(PropTypes.any).isRequired,
  isViewMode: PropTypes.bool.isRequired,
  selectedRows: PropTypes.objectOf(PropTypes.any),
  categoriesToShow: PropTypes.arrayOf(PropTypes.string),
  disclaimerText: PropTypes.string,
  isAdminView: PropTypes.bool,
  updateNoticeOfWorkApplication: PropTypes.func.isRequired,
  editNoticeOfWorkDocument: PropTypes.func.isRequired,
  sortNoticeOfWorkDocuments: PropTypes.func.isRequired,
  fetchImportedNoticeOfWorkApplication: PropTypes.func.isRequired,
  deleteNoticeOfWorkApplicationDocument: PropTypes.func.isRequired,
  allowAfterProcess: PropTypes.bool,
  disableCategoryFilter: PropTypes.bool,
  isStandardDocuments: PropTypes.bool,
  isFinalPackageTable: PropTypes.bool,
  isRefConDocuments: PropTypes.bool,
  isPackageModal: PropTypes.bool,
  isSortingAllowed: PropTypes.bool,
  showDescription: PropTypes.bool,
  lockedRowKeys: PropTypes.arrayOf(PropTypes.string),
  packageTitles: PropTypes.shape({
    titles: PropTypes.objectOf(PropTypes.string),
    onTitleChange: PropTypes.func,
  }),
  applicationDelay: PropTypes.objectOf(PropTypes.string).isRequired,
  draftPermitAmendment: PropTypes.objectOf(PropTypes.any),
  showOrderColumn: PropTypes.bool,
  groupFiguresFirst: PropTypes.bool,
  documentNumberFormat: PropTypes.oneOf(["decimal", "whole"]),
  enableFileManagement: PropTypes.bool,
  userRoles: PropTypes.arrayOf(PropTypes.string),
  createNoticeOfWorkDocumentVersion: PropTypes.func.isRequired,
  archiveNoticeOfWorkDocuments: PropTypes.func.isRequired,
  openDocument: PropTypes.func.isRequired,
};

const defaultProps = {
  selectedRows: null,
  categoriesToShow: [],
  draftPermitAmendment: null,
  disclaimerText: "",
  isAdminView: false,
  allowAfterProcess: false,
  disableCategoryFilter: false,
  isFinalPackageTable: false,
  isStandardDocuments: false,
  isRefConDocuments: false,
  isPackageModal: false,
  isSortingAllowed: false,
  showDescription: false,
  lockedRowKeys: [],
  packageTitles: null,
  documentNumberFormat: "decimal",
  showOrderColumn: false,
  groupFiguresFirst: false,
  enableFileManagement: false,
  userRoles: [],
};

const transformDocuments = (
  documents,
  now_application_guid,
  noticeOfWorkApplicationDocumentTypeOptionsHash,
  isFinalPackageTable,
  compareFn = comparePermitPackageDocuments,
  fileManagementContext = null
) =>
  documents &&
  documents
    .sort(compareFn)
    .map((document, index) => ({
      key: document.now_application_document_xref_guid,
      now_application_document_xref_guid: document.now_application_document_xref_guid,
      mine_document_guid: document.mine_document.mine_document_guid,
      now_application_guid,
      filename: document.mine_document.document_name || Strings.EMPTY_FIELD,
      document_manager_guid: document.mine_document.document_manager_guid,
      upload_date: document.mine_document.upload_date,
      category:
        (noticeOfWorkApplicationDocumentTypeOptionsHash &&
          noticeOfWorkApplicationDocumentTypeOptionsHash[
          document.now_application_document_type_code
          ]) ||
        document.documenttype ||
        Strings.EMPTY_FIELD,
      description: document.description || Strings.EMPTY_FIELD,
      is_final_package: document.is_final_package || false,
      is_referral_package: document.is_referral_package || false,
      is_consultation_package: document.is_consultation_package || false,
      isModificationAllowed:
        (!document.is_final_package &&
          !document.is_referral_package &&
          !document.is_consultation_package) ||
        isFinalPackageTable,
      index,
      ...document,
    }))
    .map((row) => (fileManagementContext ? toFileManagementRow(row, fileManagementContext) : row));

const SortableItem = sortableElement((props) => <tr {...props} />);
const SortableContainer = sortableContainer((props) => <tbody {...props} />);

export class NOWDocuments extends Component {
  getFileManagementContext = () =>
    this.props.enableFileManagement
      ? getFileManagementContext({
        noticeOfWork: this.props.noticeOfWork,
        applicationDelay: this.props.applicationDelay,
        isViewMode: this.props.isViewMode,
        userRoles: this.props.userRoles,
      })
      : null;

  getDataSource = () =>
    transformDocuments(
      this.props.documents,
      this.props.noticeOfWork.now_application_guid,
      this.props.noticeOfWorkApplicationDocumentTypeOptionsHash,
      this.props.isFinalPackageTable,
      this.props.groupFiguresFirst ? compareByTypeThenOrder : comparePermitPackageDocuments,
      this.getFileManagementContext()
    );

  state = {
    dataSource: this.getDataSource(),
  };

  componentDidUpdate = (prevProps) => {
    const fileManagementContextChanged =
      this.props.enableFileManagement &&
      (prevProps.applicationDelay !== this.props.applicationDelay ||
        prevProps.userRoles !== this.props.userRoles ||
        prevProps.isViewMode !== this.props.isViewMode ||
        prevProps.noticeOfWork.now_application_status_code !==
        this.props.noticeOfWork.now_application_status_code);
    if (prevProps.documents !== this.props.documents || fileManagementContextChanged) {
      this.setState({
        dataSource: this.getDataSource(),
      });
    }
  };

  renderDescriptionCaption = (record) =>
    this.props.showDescription &&
      record.description &&
      record.description !== Strings.EMPTY_FIELD ? (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {record.description}
      </Typography.Text>
    ) : null;

  onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const hasLockedRow = this.state.dataSource?.some((d) => d.isLockedApplicationForm);
      const targetIndex = hasLockedRow && newIndex === 0 ? 1 : newIndex;
      const newData = arrayMove([].concat(this.state.dataSource), oldIndex, targetIndex);
      newData.forEach((doc, i) => {
        if (!(hasLockedRow && doc.isLockedApplicationForm)) {
          doc.index = i;
          doc.final_package_order = i;
        }
      });
      this.setState({ dataSource: newData });
      this.handleSortDocument(newData);
    }
  };

  DraggableContainer = (props) => (
    <SortableContainer
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={this.onSortEnd}
      {...props}
    />
  );

  DraggableBodyRow = ({ className, style, ...restProps }) => {
    // Matches rows on "index", so this only finds the row when rowKey is "index" (see rowKey below)
    const index =
      this.state.dataSource &&
      this.state.dataSource.findIndex((x) => x.index === restProps["data-row-key"]);
    const isLockedRow = this.state.dataSource?.[index]?.isLockedApplicationForm;
    // Order column can now be visible without dragging being allowed — disable the actual drag capability whenever isSortingAllowed is false.
    return (
      <SortableItem
        index={index}
        disabled={isLockedRow || !this.props.isSortingAllowed}
        // Keeps the row classes that line file names up beside the version history icon
        className={this.props.enableFileManagement ? className : undefined}
        {...restProps}
      />
    );
  };

  isInCompleteStatus = () =>
    this.props.noticeOfWork.now_application_status_code === "AIA" ||
    this.props.noticeOfWork.now_application_status_code === "WDN" ||
    this.props.noticeOfWork.now_application_status_code === "REJ" ||
    this.props.noticeOfWork.now_application_status_code === "NPR" ||
    !isEmpty(this.props.applicationDelay);

  handleAddDocument = (values) => {
    const documents = values.uploadedFiles.map((file) => {
      return {
        now_application_document_type_code: values.now_application_document_type_code,
        description: values.description,
        is_final_package: values.is_final_package,
        permit_package_document_type_code: values.permit_package_document_type_code,
        preamble_title: values?.preamble_title,
        preamble_author: values?.preamble_author,
        mine_document: {
          ...file,
          mine_guid: this.props.noticeOfWork.mine_guid,
        },
      };
    });
    return this.props
      .updateNoticeOfWorkApplication(
        { documents },
        this.props.noticeOfWork.now_application_guid,
        "Successfully added documents to this application."
      )
      .then(() => {
        this.props.fetchImportedNoticeOfWorkApplication(
          this.props.noticeOfWork.now_application_guid
        );
        this.props.closeModal();
      });
  };

  handleSortDocument = (newData) => {
    const sortedDocuments = newData
      .filter((document) => !document.isLockedApplicationForm)
      .map((document, index) => ({
        mine_document_guid: document.mine_document_guid,
        final_package_order: index + 1,
      }));
    const values = { sorted_documents: sortedDocuments };
    return this.props
      .sortNoticeOfWorkDocuments(this.props.noticeOfWork.now_application_guid, values)
      .then(() =>
        this.props.fetchImportedNoticeOfWorkApplication(
          this.props.noticeOfWork.now_application_guid
        )
      );
  };

  handleEditDocument = (values) => {
    return this.props
      .editNoticeOfWorkDocument(
        this.props.noticeOfWork.now_application_guid,
        values.mine_document_guid,
        values
      )
      .then(() => {
        this.props.fetchImportedNoticeOfWorkApplication(
          this.props.noticeOfWork.now_application_guid
        );
        this.props.closeModal();
      });
  };

  handleDeleteDocument = (applicationGuid, mineDocumentGuid) => {
    return this.props
      .deleteNoticeOfWorkApplicationDocument(applicationGuid, mineDocumentGuid)
      .then(() => {
        this.props.fetchImportedNoticeOfWorkApplication(
          this.props.noticeOfWork.now_application_guid
        );
      });
  };

  openAddDocumentModal = () => {
    this.props.openModal({
      props: {
        onSubmit: this.handleAddDocument,
        now_application_guid: this.props.noticeOfWork.now_application_guid,
        title: "Add Notice of Work document",
        categoriesToShow: this.props.categoriesToShow,
        isEditMode: false,
        isInCompleteStatus: this.isInCompleteStatus(),
      },
      content: modalConfig.EDIT_NOTICE_OF_WORK_DOCUMENT,
    });
  };

  openEditDocumentModal = (record) => {
    this.props.openModal({
      props: {
        initialValues: record,
        onSubmit: this.handleEditDocument,
        now_application_guid: this.props.noticeOfWork.now_application_guid,
        title: "Edit Notice of Work document",
        categoriesToShow: this.props.categoriesToShow,
        isEditMode: true,
        isInCompleteStatus: this.isInCompleteStatus(),
      },
      content: modalConfig.EDIT_NOTICE_OF_WORK_DOCUMENT,
      width: "75vw",
    });
  };

  refreshApplication = () =>
    this.props.fetchImportedNoticeOfWorkApplication(this.props.noticeOfWork.now_application_guid);

  openReplaceModal = (record) =>
    openReplaceFileModal({
      record,
      applicationGuid: this.props.noticeOfWork.now_application_guid,
      openModal: this.props.openModal,
      createNoticeOfWorkDocumentVersion: this.props.createNoticeOfWorkDocumentVersion,
      onReplaced: this.refreshApplication,
    });

  openArchiveModal = (record) =>
    openArchiveFileModal({
      record,
      applicationGuid: this.props.noticeOfWork.now_application_guid,
      conditions: this.props.draftPermitAmendment?.conditions,
      openModal: this.props.openModal,
      closeModal: this.props.closeModal,
      archiveNoticeOfWorkDocuments: this.props.archiveNoticeOfWorkDocuments,
      onArchived: this.refreshApplication,
    });

  fileManagementActionsColumn = () =>
    fileManagementActionsColumn({
      openDocument: this.props.openDocument,
      onReplace: this.openReplaceModal,
      onArchive: this.openArchiveModal,
    });

  columns = (noticeOfWorkApplicationDocumentTypeOptions, categoriesToShow) => {
    let tableColumns = [];
    const filtered = noticeOfWorkApplicationDocumentTypeOptions.filter(({ subType, value }) => {
      if (subType && categoriesToShow.length > 0) {
        return categoriesToShow.includes(subType);
      }
      if (categoriesToShow.length > 0) {
        return categoriesToShow.includes(value);
      }
      return true;
    });

    const categoryFilters = filtered.map((item) => ({
      text: item.label,
      value: item.value,
    }));

    const sortColumn = {
      title: "Order",
      dataIndex: "index",
      className: "drag-visible",
      render: (text, record) => {
        const hasLockedRow = this.state.dataSource?.some((d) => d.isLockedApplicationForm);
        if (record.isLockedApplicationForm) {
          // The NoW application document (NTR — system-generated Notice of Work Form) is
          // always position 1.1 in the permit; it is locked and cannot be reordered.
          return (
            <span style={{ paddingLeft: "26px" }}>
              {getPermitPackageOrderLabel(text, hasLockedRow, true)}
            </span>
          );
        }
        // Order column can now be visible without dragging being allowed — hide the drag handle itself whenever isSortingAllowed is false.
        const dragHandle = this.props.isSortingAllowed ? (
          <DragHandle />
        ) : (
          <span style={{ display: "inline-block", width: 14 }} />
        );
        if (this.props.documentNumberFormat === "whole") {
          return (
            <>
              {dragHandle}
              &nbsp; {text + 1}
            </>
          );
        }
        return (
          <>
            {dragHandle}
            &nbsp; {getPermitPackageOrderLabel(text, hasLockedRow, false)}
          </>
        );
      },
    };

    const fileNameColumn = this.props.selectedRows
      ? {
        title: "File Name",
        dataIndex: "filename",
        key: "filename",
        sorter: (a, b) => (a.filename > b.filename ? -1 : 1),
        render: (text) => <div title="File Name">{text}</div>,
      }
      : {
        title: "File Name",
        onHeaderCell: this.props.enableFileManagement ? fileNameHeaderCell : undefined,
        dataIndex: "filename",
        key: "filename",
        sorter: (a, b) => (a.filename > b.filename ? -1 : 1),
        render: (text, record) => {
          if (record.isLockedApplicationForm && !record.document_manager_guid) {
            return <div title="File Name">N/A</div>;
          }
          // Clicking a previous version's file name downloads that version, it can also be viewed from the Actions menu
          if (isVersionRow(record)) {
            return (
              <div title="File Name" style={getFileNameOffset(record)}>
                <Button
                  type="link"
                  className="btn--inline-link"
                  onClick={() => downloadFileFromDocumentManager(record)}
                >
                  {record.document_name}
                </Button>
              </div>
            );
          }
          const fileName = (
            <div
              title="File Name"
              style={this.props.enableFileManagement ? getFileNameOffset(record) : {}}
            >
              <DocumentLink
                documentManagerGuid={record.document_manager_guid}
                documentName={record.filename}
                truncateDocumentName={false}
              />
              {this.renderDescriptionCaption(record)}
            </div>
          );
          return this.props.enableFileManagement
            ? documentWithTag(record, fileName, "File Name", true)
            : fileName;
        },
      };

    const descriptionColumn = {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sorter: (a, b) => (a.description > b.description ? -1 : 1),
      render: (text) => <div title="Proponent Description">{text}</div>,
    };

    const fileMetadataColumns = [
      {
        title: "Title",
        dataIndex: "preamble_title",
        key: "preamble_title",
        render: (text, record) => <div title="Title">{record.preamble_title}</div>,
      },
      {
        title: "Author",
        dataIndex: "preamble_author",
        key: "preamble_author",
        render: (text, record) => <div title="Author">{record.preamble_author}</div>,
      },
      {
        title: "Date",
        dataIndex: "preamble_date",
        key: "preamble_date",
        render: (text, record) => (
          <div title="Date">{formatDate(record.preamble_date) || "N/A"}</div>
        ),
      },
    ];

    const categoryColumn = {
      title: "Category",
      dataIndex: "category",
      key: "category",
      filters: this.props.disableCategoryFilter ? null : categoryFilters,
      onFilter: this.props.disableCategoryFilter
        ? () => { }
        : (value, record) => record.category.includes(value),
      sorter: (a, b) => (a.category > b.category ? -1 : 1),
      render: (text) => <div title="Category">{text}</div>,
    };

    const uploadDateColumn = {
      title: "Date/Time",
      dataIndex: "upload_date",
      key: "upload_date",
      sorter: (a, b) => (moment(a.upload_date) > moment(b.upload_date) ? -1 : 1),
      render: (text, record) => <div title="Due">{formatDateTime(record.upload_date)}</div>,
    };

    const deleteAndEditButtonColumn = {
      title: "",
      dataIndex: "isModificationAllowed",
      key: "isModificationAllowed",
      width: 170,
      render: (isModificationAllowed, record) => {
        if (record.isLockedApplicationForm || isVersionRow(record)) {
          return <div />;
        }
        if (this.props.isFinalPackageTable && this.props.isViewMode) {
          return <div />;
        }
        if (!this.isInCompleteStatus()) {
          if (isModificationAllowed) {
            return (
              <NOWActionWrapper
                permission={Permission.EDIT_PERMITS}
                tab={this.props.isAdminView ? "" : "REV"}
                ignoreDelay
              >
                {!this.props.isFinalPackageTable && (
                  <Popconfirm
                    placement="topLeft"
                    title={
                      isFileReferencedInConditions(
                        this.props.draftPermitAmendment?.conditions,
                        record.now_application_document_xref_guid
                      )
                        ? "This file is currently being referenced in a permit condition. Deleting it will break that reference. Are you sure you want to delete this document?"
                        : "Are you sure you want to remove this document?"
                    }
                    okText="Delete"
                    cancelText="Cancel"
                    onConfirm={() =>
                      this.handleDeleteDocument(
                        record.now_application_guid,
                        record.mine_document_guid
                      )
                    }
                  >
                    <Button className="no-margin" ghost type="primary" size="small">
                      <img name="remove" src={TRASHCAN} alt="Remove document" />
                    </Button>
                  </Popconfirm>
                )}
                <Button
                  className="no-margin"
                  ghost
                  type="primary"
                  size="small"
                  onClick={() => this.openEditDocumentModal(record)}
                >
                  <img name="remove" src={EDIT_OUTLINE_VIOLET} alt="Edit document" />
                </Button>
              </NOWActionWrapper>
            );
          }
          return (
            <div disabled onClick={(event) => event.stopPropagation()}>
              <NOWActionWrapper
                permission={Permission.EDIT_PERMITS}
                tab={this.props.isAdminView ? "" : "REV"}
                ignoreDelay
              >
                <Tooltip
                  title="You cannot remove a document that is a part of the Permit, Referral, or Consultation Package."
                  placement="right"
                  mouseEnterDelay={0.3}
                  className="no-margin"
                >
                  <Button className="no-margin" ghost type="primary" size="small">
                    <img
                      className="lessOpacity"
                      name="remove"
                      src={TRASHCAN}
                      alt="Remove document"
                    />
                  </Button>
                </Tooltip>
                <Tooltip
                  title="You cannot edit a document that is a part of the Permit, Referral, or Consultation Package."
                  placement="right"
                  mouseEnterDelay={0.3}
                  className="no-margin"
                >
                  <Button className="no-margin" ghost type="primary" size="small">
                    <img
                      className="lessOpacity"
                      name="remove"
                      src={EDIT_OUTLINE_VIOLET}
                      alt="Edit document"
                    />
                  </Button>
                </Tooltip>
              </NOWActionWrapper>
            </div>
          );
        }
        return <div />;
      },
    };

    const permitPackageColumn = {
      width: 150,
      title: () => {
        return !this.isInCompleteStatus() ? (
          <div className="inline-flex between">
            <div className="grid">
              <span>Permit</span>
              <span>Package</span>
            </div>
            <PermitPackage isAdminView={this.props.isAdminView} isTableHeaderView />
          </div>
        ) : (
          <div className="grid">
            <span>Permit</span>
            <span>Package</span>
          </div>
        );
      },
      dataIndex: "is_final_package",
      key: "is_final_package",
      render: (text, record) =>
        isVersionRow(record) ? null : <div title="Part of Permit">{text ? "Yes" : "No"}</div>,
    };

    const consultationPackageColumn = {
      width: 150,
      title: () => {
        return !this.isInCompleteStatus() ? (
          <div className="inline-flex between">
            <div className="grid">
              <span>Consultation</span>
              <span>Package</span>
            </div>
            <ReferralConsultationPackage type="CON" isTableHeaderView />
          </div>
        ) : (
          <div className="grid">
            <span>Consultation</span>
            <span>Package</span>
          </div>
        );
      },
      dataIndex: "is_consultation_package",
      key: "is_consultation_package",
      render: (text, record) =>
        isVersionRow(record) ? null : <div title="Consultation Package">{text ? "Yes" : "No"}</div>,
    };

    const referralPackageColumn = {
      width: 150,
      title: () => {
        return !this.isInCompleteStatus() ? (
          <div className="inline-flex between">
            <div className="grid">
              <span>Referral</span>
              <span>Package</span>
            </div>
            <ReferralConsultationPackage type="REF" isTableHeaderView />
          </div>
        ) : (
          <div className="grid">
            <span>Referral</span>
            <span>Package</span>
          </div>
        );
      },
      dataIndex: "is_referral_package",
      key: "is_referral_package",
      render: (text, record) =>
        isVersionRow(record) ? null : <div title="Referral Package">{text ? "Yes" : "No"}</div>,
    };

    const postApprovalDocumentColumn = {
      title: "",
      key: "post_approval_document",
      render: (text, record) => {
        if (isVersionRow(record)) {
          return null;
        }
        let isPostDecision = false;
        if (
          this.isInCompleteStatus() &&
          moment(record.upload_date, "YYYY-MM-DD") >
          moment(this.props.noticeOfWork.decision_by_user_date, "YYYY-MM-DD")
        ) {
          isPostDecision = true;
        }
        return (
          isPostDecision && (
            <Tooltip
              title="This is a post-decision document."
              placement="right"
              mouseEnterDelay={0.3}
            >
              <FlagOutlined />
            </Tooltip>
          )
        );
      },
    };

    if (this.props.isFinalPackageTable) {
      tableColumns = [
        ...fileMetadataColumns,
        categoryColumn,
        fileNameColumn,
        descriptionColumn,
        deleteAndEditButtonColumn,
      ];
      if (this.props.showOrderColumn) {
        tableColumns = [sortColumn, ...tableColumns];
      }
      if (this.props.enableFileManagement) {
        tableColumns = [...tableColumns, this.fileManagementActionsColumn()];
      }
    } else if (this.props.isStandardDocuments) {
      tableColumns = [categoryColumn, fileNameColumn, uploadDateColumn];
      if (this.isInCompleteStatus()) {
        tableColumns = [...tableColumns, postApprovalDocumentColumn];
      }
      tableColumns = [
        ...tableColumns,
        deleteAndEditButtonColumn,
        referralPackageColumn,
        consultationPackageColumn,
        permitPackageColumn,
      ];
      if (this.props.enableFileManagement) {
        tableColumns = [...tableColumns, this.fileManagementActionsColumn()];
      }
    } else if (this.props.isRefConDocuments) {
      tableColumns = [categoryColumn, fileNameColumn];
      if (this.isInCompleteStatus()) {
        tableColumns = [...tableColumns, postApprovalDocumentColumn];
      }
      tableColumns = [...tableColumns, uploadDateColumn];
    } else if (this.props.isPackageModal) {
      // The Title column is only shown in the permit package bulk modal,
      // Other bulk modals (e.g. consultation) are unchanged.
      const titleColumns = this.props.packageTitles
        ? [
          renderPermitPackageTitleColumn({
            selectedKeys: this.props.selectedRows?.selectedCoreRows,
            lockedRowKeys: this.props.lockedRowKeys,
            packageTitles: this.props.packageTitles,
          }),
        ]
        : [];
      tableColumns = [
        fileNameColumn,
        ...titleColumns,
        categoryColumn,
        descriptionColumn,
        uploadDateColumn,
      ];
    } else {
      tableColumns = [categoryColumn, fileNameColumn, uploadDateColumn];
    }

    return tableColumns;
  };

  render() {
    const columns = this.columns(
      this.props.noticeOfWorkApplicationDocumentTypeOptions,
      this.props.categoriesToShow
    );
    const showDownloadAll =
      this.props.enableFileManagement &&
      !this.props.selectedRows &&
      !this.props.isFinalPackageTable &&
      !this.props.isRefConDocuments;
    const addDocumentButton = !this.props.selectedRows &&
      !this.props.isViewMode &&
      !this.props.isRefConDocuments &&
      !this.props.isFinalPackageTable && (
        <NOWActionWrapper
          permission={Permission.EDIT_PERMITS}
          tab={this.props.isAdminView ? "" : "REV"}
          allowAfterProcess={this.props.allowAfterProcess}
          ignoreDelay
        >
          <AddButton
            // Sits beside Download All when it's shown, otherwise in its usual spot
            className={showDownloadAll ? undefined : "position-right"}
            disabled={this.props.isViewMode}
            style={this.props.isAdminView && !showDownloadAll ? { marginRight: "100px" } : {}}
            onClick={this.openAddDocumentModal}
          >
            Add Document
          </AddButton>
        </NOWActionWrapper>
      );
    return (
      <div>
        <Row className="inline-flex between">
          <Col span={16}>
            <p>{this.props.disclaimerText}</p>
          </Col>
          <Col span={6}>
            {showDownloadAll ? (
              <div className="now-table-header-actions">
                <NOWDownloadAllButton
                  rows={this.state.dataSource ?? []}
                  mineGuid={this.props.noticeOfWork.mine_guid}
                  documentType="government-documents"
                />
                {addDocumentButton}
              </div>
            ) : (
              addDocumentButton
            )}
          </Col>
        </Row>
        <br />
        <CoreTable
          columns={columns}
          recordType="document description"
          dataSource={this.state.dataSource}
          // The key must be set to "index" to allow the drag-sort to work.
          // With file management on, rows are keyed by version guid or key instead (so version history can expand), which
          // DraggableBodyRow can't match, so drag-sort won't work. That's fine for now, as file management is only on where sorting isn't allowed.
          rowKey={
            this.props.enableFileManagement
              ? (record) => record.mine_document_version_guid ?? record.key
              : this.props.isSortingAllowed
                ? "index"
                : "key"
          }
          expandProps={
            this.props.enableFileManagement
              ? {
                childrenColumnName: "versions",
                matchChildColumnsToParent: true,
                recordDescription: "version history",
                rowExpandable: (record) => record.number_prev_versions > 0,
                // Show the expand icon beside the file name, wherever it sits in the table
                expandIconColumnIndex: columns.findIndex((column) => column.key === "filename"),
              }
              : null
          }
          components={{
            body: {
              wrapper: this.DraggableContainer,
              row: this.DraggableBodyRow,
            },
          }}
          rowSelection={
            this.props.selectedRows
              ? {
                selectedRowKeys: this.props.selectedRows.selectedCoreRows,
                onChange: (selectedRowKeys) => {
                  this.props.selectedRows.setSelectedCoreRows(selectedRowKeys);
                },
                getCheckboxProps: (record) => ({
                  disabled: this.props.lockedRowKeys?.includes(record.key),
                }),
              }
              : null
          }
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  noticeOfWorkApplicationDocumentTypeOptionsHash: getNoticeOfWorkApplicationDocumentTypeOptionsHash(
    state
  ),
  noticeOfWorkApplicationDocumentTypeOptions: getDropdownNoticeOfWorkApplicationDocumentTypeOptions(
    state
  ),
  noticeOfWork: getNoticeOfWork(state),
  applicationDelay: getApplicationDelay(state),
  draftPermitAmendment: getDraftPermitAmendmentForNOW(state),
  userRoles: getUserAccessData(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      openModal,
      closeModal,
      updateNoticeOfWorkApplication,
      fetchImportedNoticeOfWorkApplication,
      deleteNoticeOfWorkApplicationDocument,
      editNoticeOfWorkDocument,
      sortNoticeOfWorkDocuments,
      createNoticeOfWorkDocumentVersion,
      archiveNoticeOfWorkDocuments,
      openDocument,
    },
    dispatch
  );

NOWDocuments.propTypes = propTypes;
NOWDocuments.defaultProps = defaultProps;

export default connect(mapStateToProps, mapDispatchToProps)(NOWDocuments);
